import { useState } from "react";
import { motion } from "framer-motion";
import { Buildings, Plus, Student, UsersThree, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtDate } from "../lib/api";
import { PageHeader, StatCard, GlassCard, Modal, Field, Loader, Badge, fadeUp } from "../components/kit";
import { Switch } from "../components/ui/switch";
import { useT } from "../lib/i18n";
import { LoginHistory } from "../components/LoginHistory";

const BLANK = { name: "", code: "", city: "", phone: "", email: "", admin_name: "", admin_email: "", admin_password: "" };

function SchoolForm({ open, setOpen, onDone }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/schools", f);
      toast.success(`${f.name} created`);
      setF(BLANK);
      setOpen(false);
      onDone();
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  const fields = [["name", "School name"], ["code", "Short code (e.g. DPS)"], ["city", "City"], ["phone", "Phone"], ["email", "School email"],
    ["admin_name", "Principal / admin name"], ["admin_email", "Admin login email"], ["admin_password", "Admin password"]];
  return (
    <Modal open={open} onOpenChange={setOpen} title="Add a school" description="Creates an isolated school workspace with its own admin." testid="add-school-dialog" wide>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(([k, l]) => (
          <Field key={k} label={l}>
            <input className="field" required={["name", "code", "admin_name", "admin_email", "admin_password"].includes(k)}
              type={k === "admin_password" ? "text" : k.includes("email") ? "email" : "text"} minLength={k === "admin_password" ? 6 : undefined}
              value={f[k]} onChange={set(k)} data-testid={`school-${k}-input`} />
          </Field>
        ))}
        <button className="btn-primary sm:col-span-2" disabled={busy} data-testid="school-submit-btn">{busy ? "Creating…" : "Create school"}</button>
      </form>
    </Modal>
  );
}

function DemoCleanup({ onDone }) {
  const { data, reload } = useApi("/platform/demo-schools");
  const [busy, setBusy] = useState(false);
  if (!data?.schools.length) return null;
  const run = async () => {
    const typed = window.prompt(`This permanently deletes ${data.schools.join(" and ")} with all their students, staff, fees and logins.\n\nType REMOVE DEMO to confirm.`);
    if (typed === null) return;
    setBusy(true);
    try {
      const r = await api.post("/platform/remove-demo-schools", { confirm: typed });
      toast.success(`Removed ${r.data.removed.length} demo school(s)`);
      reload();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-10 flex flex-col gap-3 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="demo-cleanup">
      <div className="text-sm text-amber-100"><b>Demo schools present:</b> {data.schools.join(", ")}. Their demo logins use publicly known passwords.
        {data.demo_mode && <span className="block text-xs text-amber-200/70">This environment is in demo mode, so removal is disabled here (use it on the live app).</span>}</div>
      <button className="btn-ghost !border-rose-400/40 !text-rose-200 shrink-0" disabled={busy || data.demo_mode} onClick={run} data-testid="remove-demo-btn">Remove demo schools</button>
    </div>
  );
}

export default function Platform() {
  const { t } = useT();
  const { data, loading, reload } = useApi("/platform/schools");
  const { data: stats, reload: reloadStats } = useApi("/platform/stats");
  const [open, setOpen] = useState(false);
  const toggle = async (s) => {
    try {
      await api.patch(`/platform/schools/${s.id}`, { active: !s.active });
      toast.success(`${s.name} ${s.active ? "disabled" : "enabled"}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Platform console" title={t("Schools")} subtitle="Every school runs in its own sealed workspace — data never crosses between them."
        actions={<button className="btn-primary" onClick={() => setOpen(true)} data-testid="add-school-btn"><Plus size={16} weight="bold" /> {t("Add school")}</button>} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard icon={Buildings} label="Schools" value={stats?.schools ?? "—"} testid="stat-schools" />
        <StatCard icon={CheckCircle} label="Active" value={stats?.active_schools ?? "—"} tone="green" testid="stat-active" delay={0.05} />
        <StatCard icon={Student} label="Students" value={stats?.students ?? "—"} tone="orange" testid="stat-students" delay={0.1} />
        <StatCard icon={UsersThree} label="Staff" value={stats?.staff ?? "—"} tone="rose" testid="stat-staff" delay={0.15} />
      </div>
      {loading ? <Loader /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {data?.map((s, i) => (
            <motion.div key={s.id} {...fadeUp(i)}>
              <GlassCard hover className="p-6 h-full" data-testid={`school-card-${s.code}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Badge status={s.active ? "paid" : "draft"}>{s.active ? "Active" : "Disabled"}</Badge>
                    <h3 className="mt-3 font-display text-xl font-semibold text-white">{s.name}</h3>
                    <p className="text-sm text-zinc-400">{s.code} · {s.city || "—"}</p>
                  </div>
                  <Switch checked={s.active} onCheckedChange={() => toggle(s)} data-testid={`school-toggle-${s.code}`} />
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-2xl bg-white/5 p-3"><div className="eyebrow !text-[10px]">Students</div><div className="mt-1 font-display text-xl">{s.student_count}</div></div>
                  <div className="rounded-2xl bg-white/5 p-3"><div className="eyebrow !text-[10px]">Staff</div><div className="mt-1 font-display text-xl">{s.staff_count}</div></div>
                </div>
                <div className="mt-5 text-xs text-zinc-400 space-y-1">
                  <div>Admin: <span className="text-zinc-200">{s.admin?.email || "—"}</span></div>
                  <div>Payments: <span className={s.payments_connected ? "text-emerald-300" : "text-zinc-500"}>{s.payments_connected ? "Razorpay connected" : "Not connected"}</span></div>
                  <div>Created {fmtDate(s.created_at)}</div>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      )}
      <SchoolForm open={open} setOpen={setOpen} onDone={() => { reload(); reloadStats(); }} />
      <DemoCleanup onDone={() => { reload(); reloadStats(); }} />
      <LoginHistory schools={data || []} />
    </div>
  );
}
